import React, { useState, useEffect } from 'react';
import { Table, Card, Input, Button, message, Spin, Row, Col, Select } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { getMarketingList } from '../../core/api/marketing';
import * as XLSX from 'xlsx';

// Placeholder API function - this will need to be implemented in the marketing API file
const getMarketingCostBreakdowns = async () => {
  // This is a placeholder - implement actual API call
  console.log('Fetching marketing cost breakdowns');
  return { 
    data: [] // Return empty array for now
  };
};

const { Option } = Select;

const MarketingCostBreakdownList = () => {
  const [loading, setLoading] = useState(false);
  const [marketings, setMarketings] = useState([]);
  const [costBreakdowns, setCostBreakdowns] = useState([]);
  const [filteredBreakdowns, setFilteredBreakdowns] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedMarketing, setSelectedMarketing] = useState(null);
  const selectedClients = useSelector((state) => state.selectedClients);

  useEffect(() => {
    fetchMarketings();
    fetchCostBreakdowns();
  }, [selectedClients]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMarketings = async () => {
    setLoading(true);
    try {
      if (selectedClients && selectedClients.length > 0) {
        const response = await getMarketingList(selectedClients, 1, 1000);
        setMarketings(response.data.results);
      }
    } catch (error) {
      console.error('Error fetching marketings:', error);
      message.error('Failed to fetch marketings');
    } finally {
      setLoading(false);
    }
  };

  const fetchCostBreakdowns = async () => {
    setLoading(true);
    try {
      const response = await getMarketingCostBreakdowns();
      setCostBreakdowns(response.data);
      setFilteredBreakdowns(response.data);
    } catch (error) {
      console.error('Error fetching marketing cost breakdowns:', error);
      message.error('Failed to fetch marketing cost breakdowns');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    filterData(value, selectedMarketing);
  };

  const handleMarketingChange = (value) => {
    setSelectedMarketing(value);
    filterData(searchText, value);
  };

  const filterData = (searchValue, marketingId) => {
    let filtered = [...costBreakdowns];
    
    if (searchValue) {
      filtered = filtered.filter(item => 
        (item.marketing_name && item.marketing_name.toLowerCase().includes(searchValue)) ||
        (item.cost_type && item.cost_type.toLowerCase().includes(searchValue))
      );
    }
    
    if (marketingId) {
      filtered = filtered.filter(item => item.marketing_id === marketingId);
    }
    
    setFilteredBreakdowns(filtered);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredBreakdowns);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Marketing Cost Breakdowns');
    
    // Generate file name with date
    const date = new Date().toISOString().split('T')[0];
    const fileName = `Marketing_Cost_Breakdowns_${date}.xlsx`;
    
    XLSX.writeFile(workbook, fileName);
    message.success(`Exported to ${fileName}`);
  };

  const calculateTotalCost = (record) => {
    const mediaCost = parseFloat(record.media_cost || 0);
    const creativeCost = parseFloat(record.creative_cost || 0);
    const productionCost = parseFloat(record.production_cost || 0);
    const agencyFee = parseFloat(record.agency_fee || 0);
    const otherCost = parseFloat(record.other_cost || 0);
    
    return (mediaCost + creativeCost + productionCost + agencyFee + otherCost).toFixed(2);
  };

  const columns = [
    {
      title: 'Marketing Code',
      dataIndex: 'marketing_code',
      key: 'marketing_code',
      sorter: (a, b) => a.marketing_code.localeCompare(b.marketing_code)
    },
    {
      title: 'Marketing Name',
      dataIndex: 'marketing_name',
      key: 'marketing_name',
      sorter: (a, b) => a.marketing_name.localeCompare(b.marketing_name)
    },
    {
      title: 'Media Cost',
      dataIndex: 'media_cost',
      key: 'media_cost',
      render: (text, record) => `${parseFloat(text || 0).toFixed(2)} ${record.media_cost_value_type === 'percentage' ? '%' : '$'}`,
      sorter: (a, b) => parseFloat(a.media_cost || 0) - parseFloat(b.media_cost || 0)
    },
    {
      title: 'Creative Cost',
      dataIndex: 'creative_cost',
      key: 'creative_cost',
      render: (text, record) => `${parseFloat(text || 0).toFixed(2)} ${record.creative_cost_value_type === 'percentage' ? '%' : '$'}`,
      sorter: (a, b) => parseFloat(a.creative_cost || 0) - parseFloat(b.creative_cost || 0)
    },
    {
      title: 'Production Cost',
      dataIndex: 'production_cost',
      key: 'production_cost',
      render: (text, record) => `${parseFloat(text || 0).toFixed(2)} ${record.production_cost_value_type === 'percentage' ? '%' : '$'}`,
      sorter: (a, b) => parseFloat(a.production_cost || 0) - parseFloat(b.production_cost || 0)
    },
    {
      title: 'Agency Fee',
      dataIndex: 'agency_fee',
      key: 'agency_fee',
      render: (text, record) => `${parseFloat(text || 0).toFixed(2)} ${record.agency_fee_value_type === 'percentage' ? '%' : '$'}`,
      sorter: (a, b) => parseFloat(a.agency_fee || 0) - parseFloat(b.agency_fee || 0)
    },
    {
      title: 'Other Cost',
      dataIndex: 'other_cost',
      key: 'other_cost',
      render: (text, record) => `${parseFloat(text || 0).toFixed(2)} ${record.other_cost_value_type === 'percentage' ? '%' : '$'}`,
      sorter: (a, b) => parseFloat(a.other_cost || 0) - parseFloat(b.other_cost || 0)
    },
    {
      title: 'Total Cost',
      key: 'total_cost',
      render: (_, record) => `$${calculateTotalCost(record)}`,
      sorter: (a, b) => parseFloat(calculateTotalCost(a)) - parseFloat(calculateTotalCost(b))
    },
    {
      title: 'Last Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text) => text ? new Date(text).toLocaleDateString() : 'N/A',
      sorter: (a, b) => new Date(a.updated_at || 0) - new Date(b.updated_at || 0)
    }
  ];

  return (
    <Card title="Marketing Cost Breakdowns" className="cost-breakdown-card">
      <Spin spinning={loading}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input
              placeholder="Search by marketing name or cost type"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={handleSearch}
              allowClear
            />
          </Col>
          <Col span={8}>
            <Select
              placeholder="Filter by Marketing"
              style={{ width: '100%' }}
              onChange={handleMarketingChange}
              allowClear
            >
              {marketings.map(marketing => (
                <Option key={marketing.id} value={marketing.id}>{marketing.code_offer}</Option>
              ))}
            </Select>
          </Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
              disabled={filteredBreakdowns.length === 0}
            >
              Export to Excel
            </Button>
          </Col>
        </Row>
        
        <Table
          columns={columns}
          dataSource={filteredBreakdowns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
          }}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Spin>
    </Card>
  );
};

export default MarketingCostBreakdownList;
